import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppContext } from '../context/AppContext';
import { i18n } from '../i18n/i18n';
import ApprovalMessage from './kid/ApprovalMessage';
import { pickStarGoal } from '../utils/starGoal';

/**
 * Login-time notifications:
 * - Non-admin members see a full-screen "Jens said yes! +5" message for each of their tasks
 *   that was approved since they last saw it (tracked via tasks.verification_seen_at). It stays
 *   until the child taps — it used to vanish after 6 seconds (design review), and that also
 *   marked it as seen, so a child who missed it never got it back. One at a time, oldest first.
 * - Admins see a persistent banner while any task is awaiting verification. It has no close
 *   button — it clears itself once the queue is empty, since it's derived live from task state
 *   rather than a one-time "seen" flag.
 */
const NotificationCenter: React.FC = () => {
  const { current_user, current_family, ackTaskVerification } = useAppContext();
  const navigate = useNavigate();
  const location = useLocation();
  // On Home the parent's approvals card says the same thing; the banner made it read twice.
  // Home has its own approvals card and /approve is the list itself, so no banner there.
  const onHome = ['/home', '/', '/approve'].includes(location.pathname);
  const [locallyAcked, setLocallyAcked] = useState<Set<string>>(new Set());
  // Several approvals at once are shown as "1 / 3", "2 / 3"… — remember the batch size.
  const batchSizeRef = useRef(0);

  const celebrationQueue = useMemo(() => {
    if (!current_family || !current_user || current_user.is_admin) return [];
    return current_family.tasks
      .filter(
        (task: any) =>
          task.completed_by === current_user.id &&
          task.verified &&
          !task.verification_seen_at &&
          !locallyAcked.has(task.id),
      )
      .sort((a: any, b: any) => (a.verified_at?.getTime?.() ?? 0) - (b.verified_at?.getTime?.() ?? 0));
  }, [current_family, current_user, locallyAcked]);

  const activeTask = celebrationQueue[0] as any | undefined;

  const pendingVerificationCount = useMemo(() => {
    if (!current_family || !current_user?.is_admin) return 0;
    return current_family.tasks.filter((task: any) => task.is_completed && !task.verified).length;
  }, [current_family, current_user]);

  const dismiss = useCallback(
    (taskId: string) => {
      setLocallyAcked((prev) => new Set(prev).add(taskId));
      ackTaskVerification(taskId).catch((err) => console.error('Failed to acknowledge task verification:', err));
    },
    [ackTaskVerification],
  );


  useEffect(() => {
    if (celebrationQueue.length === 0) batchSizeRef.current = 0;
    else if (batchSizeRef.current < celebrationQueue.length) batchSizeRef.current = celebrationQueue.length;
  }, [celebrationQueue.length]);

  if (!current_family || !current_user) return null;

  const me = current_family.members.find((m: any) => m.id === current_user.id);
  const totalStars = Number(me?.points ?? current_user.points ?? 0);
  const batchSize = batchSizeRef.current || celebrationQueue.length;

  return (
    <>
      {pendingVerificationCount > 0 && !onHome && (
        <button
          type="button"
          onClick={() => navigate('/approve')}
          className="sticky top-0 z-30 w-full flex items-center justify-center gap-2 bg-kid-orange-bg dark:bg-amber-900/30 hover:bg-kid-orange/30 dark:hover:bg-amber-900/50 text-kid-text dark:text-kid-text-dark px-4 py-2.5 text-sm font-semibold text-center transition-colors"
        >
          <span aria-hidden>🔔</span>
          <span>
            {pendingVerificationCount === 1
              ? i18n.t('notifications.admin_pending_verification_one')
              : i18n.t('notifications.admin_pending_verification_other').replace('{{count}}', String(pendingVerificationCount))}
          </span>
          <span aria-hidden>→</span>
        </button>
      )}

      {activeTask && (
        <ApprovalMessage
          key={activeTask.id}
          approverName={
            current_family.members.find((m: any) => m.id === activeTask.verified_by)?.name || i18n.t('rewards.unknown_member')
          }
          taskTitle={activeTask.title}
          points={Number(activeTask.points) || 0}
          totalStars={totalStars}
          note={activeTask.approval_note ? i18n.t(`approve.note_${activeTask.approval_note}`) : null}
          goal={pickStarGoal(current_family.rewards, current_user.id)}
          onDone={() => dismiss(activeTask.id)}
          onSeeReward={() => {
            dismiss(activeTask.id);
            navigate('/rewards');
          }}
          queueIndex={batchSize - celebrationQueue.length + 1}
          queueSize={batchSize}
        />
      )}
    </>
  );
};

export default NotificationCenter;
